import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { Col, Row, Card } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import Calendar from "components/common/Calendar";
import {
  GetItemError,
  GetServiceCodeDescription,
} from "../config/preauthorizationApi";
import { setErrorServices } from "../config/preauthorizationSlice";

const ServicesProceduresPayerResponse = ({ row, show }) => {
  const dispatch = useDispatch();

  // Preauthorization state selectors
  const {
    preauthorizationRequest,
    preauthorizationResponse,
    preauthorizationCancelResponse,
    viewPreauthorizationInfo,
    errorItemMessage,
  } = useSelector((state) => state.preauthorization);

  // Claim state selectors
  const {
    claimResponseData,
    claimPreauthResponseData,
    claimCancelResponseData,
    viewClaimInfo,
    claimRequestData,
  } = useSelector((state) => state.claim);

  // Determine if we're in claim or preauth context
  const isClaimContext = !!claimRequestData;
  const isAdvanced = viewPreauthorizationInfo?.preauthadv_id;
  const [errorMsg, setErrorMsg] = useState(false);
  // Get relevant data based on context
  const { request, response, cancellation, viewInfo } = useMemo(() => {
    if (isClaimContext) {
      return {
        request: claimRequestData,
        response: claimResponseData || claimPreauthResponseData,
        cancellation: claimCancelResponseData,
        viewInfo: viewClaimInfo,
      };
    }
    return {
      request: preauthorizationRequest,
      response: preauthorizationResponse,
      cancellation: preauthorizationCancelResponse,
      viewInfo: viewPreauthorizationInfo,
    };
  }, [
    isClaimContext,
    claimRequestData,
    claimResponseData,
    claimPreauthResponseData,
    claimCancelResponseData,
    viewClaimInfo,
    preauthorizationRequest,
    preauthorizationResponse,
    preauthorizationCancelResponse,
    viewPreauthorizationInfo,
  ]);

  // Extract claim resource
  const claimResource = useMemo(
    () =>
      request?.entry?.find((e) => e.resource?.resourceType === "Claim")
        ?.resource,
    [request]
  );

  // Extract claim response
  const claimResponse = useMemo(() => {
    // First try to get ClaimResponse directly from response
    const directClaimResponse = response?.entry?.find(
      (e) => e.resource?.resourceType === "ClaimResponse"
    )?.resource;

    if (directClaimResponse) return directClaimResponse;

    // If not found, check inside a Bundle
    const bundle = response?.entry?.find(
      (item) => item?.resource?.resourceType === "Bundle"
    )?.resource;

    return bundle?.entry?.find(
      (item) => item?.resource?.resourceType === "ClaimResponse"
    )?.resource;
  }, [response]);

  console.log({ claimResource, claimResponse });

  const claimMedicals =
    preauthorizationRequest?.entry
      ?.filter((e) => e.resource?.resourceType === "MedicationRequest")
      ?.map((e) => e.resource) ||
    claimRequestData?.entry
      ?.filter((e) => e.resource?.resourceType === "MedicationRequest")
      ?.map((e) => e.resource) ||
    [];

  // Advanced preauthorization handling
  const advancedClaimResponse = useMemo(() => {
    const bundle = response?.entry?.find(
      (item) => item?.resource?.resourceType === "Bundle"
    )?.resource;
    return bundle?.entry?.find(
      (item) => item?.resource?.resourceType === "ClaimResponse"
    )?.resource;
  }, [response]);

  const [diagnosisDescriptions, setDiagnosisDescriptions] = useState({});
  const [itemErrorMessages, setItemErrorMessages] = useState({});

  console.log({ itemErrorMessages });

  // Practitioner data
  const practitioner = useMemo(
    () =>
      request?.entry?.filter(
        (e) => e.resource?.resourceType === "Practitioner"
      ),
    [request]
  );

  console.log({ practitioner });

  // Transform FHIR data to service items
  const { serviceData, itemErrorCodes } = useMemo(() => {
    if (!claimResource?.item) return { serviceData: [], itemErrorCodes: {} };

    const serviceData = [];
    const errorCodes = {};

    let medicationIndex = 0;

    claimResource.item.forEach((entry, index) => {
      const viewService = viewPreauthorizationInfo?.service_procedures?.[index];
      const responseItem = claimResponse?.item?.find(
        (item) => item?.itemSequence === entry?.sequence
      );

      // Extract reason codes for this item
      const rejectionReasons = responseItem?.adjudication
        ?.filter((adj) => adj.reason?.coding?.length > 0)
        .map((adj) => adj.reason.coding[0].display)
        .filter((display) => display);

      const statusExtension = responseItem?.extension?.find((e) =>
        e.url.includes("adjudication-outcome")
      );

      const toothNo = entry?.bodySite?.coding?.[0]?.code || "-";

      const approvedQty = responseItem?.adjudication?.find(
        (item) => item?.category?.coding?.[0]?.code === "approved-quantity"
      )?.value;

      const status =
        statusExtension?.valueCodeableConcept?.coding?.[0]?.code || "pending";

      const getExtensionValue = (urlPart) =>
        entry.extension?.find((ext) => ext?.url?.includes(urlPart));

      const maternityExt = getExtensionValue("extension-maternity");
      const vatExt = getExtensionValue("extension-tax");
      const invoiceNo = getExtensionValue("patientInvoice");
      const prescripedMedication = getExtensionValue(
        "extension-prescribed-Medication"
      );
      const pharmacySubstitue = getExtensionValue(
        "extension-pharmacist-substitute"
      );
      const selectionReason = getExtensionValue("extension-Selection-Reason");
      const strength = getExtensionValue("extension-strength");
      const absenceReason = getExtensionValue(
        "extension-absence-of-scientific-code"
      );

      const patientShare = responseItem?.adjudication?.find(
        (item) => item?.category?.coding?.[0]?.code === "copay"
      )?.amount?.value;

      const eligibleAmount = responseItem?.adjudication?.find(
        (item) => item?.category?.coding?.[0]?.code === "eligible"
      )?.amount?.value;

      const unitPrice = parseFloat(entry.unitPrice?.value || 0);
      const quantity = parseFloat(entry.quantity?.value || 0);
      const factor = parseFloat(entry.factor || 1);
      const discount = unitPrice * quantity * (1 - factor);
      const strengthValue = strength?.valueString;
      const diagnosisSequences = entry.diagnosisSequence || [];
      const diagnosisCodes = diagnosisSequences
        .map((seq) => {
          const diag = claimResource.diagnosis?.find((d) => d.sequence === seq);
          return diag?.diagnosisCodeableConcept?.coding?.[0]?.code;
        })
        .filter((code) => code)
        .join(", ");

      const eligibilityAmt = responseItem?.adjudication?.find(
        (item) => item?.category?.coding?.[0]?.code === "eligible"
      )?.amount?.value;
      const benefitAmt = responseItem?.adjudication?.find(
        (item) => item?.category?.coding?.[0]?.code === "benefit"
      )?.amount?.value;

      const medCode =
        prescripedMedication?.valueCodeableConcept?.coding?.[0]?.code || "-";
      const substitutePharmacy =
        pharmacySubstitue?.valueCodeableConcept?.text || "-";
      const otherSubstituePharmacy =
        pharmacySubstitue?.valueCodeableConcept?.coding?.[0]?.code || "-";
      const selectReason =
        selectionReason?.valueCodeableConcept?.coding?.[0]?.code || "-";
      const absenceRsn =
        absenceReason?.valueCodeableConcept?.coding?.[0]?.code || "-";

      const serviceSystem = entry.productOrService?.coding?.[0]?.system || "";
      const type = !serviceSystem.includes("medication-codes")
        ? "Procedures"
        : "Medication";

      let claimMedical = null;
      let claimMedicalCode = "";
      let claimMedicalDisplay = "";
      let claimRoute = "";
      let dosageData = null;

      if (type === "Medication" && medicationIndex < claimMedicals?.length) {
        claimMedical = claimMedicals[medicationIndex];
        claimMedicalCode =
          claimMedical?.medicationCodeableConcept?.coding?.[0]?.code || "";
        claimMedicalDisplay =
          claimMedical?.medicationCodeableConcept?.coding?.[0]?.display || "";
        claimRoute =
          claimMedical?.dosageInstruction?.[0]?.route?.coding?.[0]?.display ||
          "";
        dosageData = claimMedical?.dosageInstruction?.[0];
        medicationIndex++;
      }

      const periodStartDate = dosageData?.timing?.repeat?.boundsPeriod?.start;
      const periodEndDate = dosageData?.timing?.repeat?.boundsPeriod?.end;
      const duration = dosageData?.timing?.repeat?.duration;
      const durationType = dosageData?.timing?.repeat?.durationUnit;
      const frequency = dosageData?.timing?.repeat?.frequency;
      const period = dosageData?.timing?.repeat?.period;
      const periodType = dosageData?.timing?.repeat?.periodUnit;
      const dose = dosageData?.doseAndRate?.[0]?.doseQuantity?.value;
      const doseType = dosageData?.doseAndRate?.[0]?.doseQuantity?.code;
      const timeInstruction = timeInstructionData?.find(
        (item) =>
          item?.time_instCode === dosageData?.timing?.code?.coding[0]?.code
      )?.time_instDisplay;

      const refill = dosageData?.timing?.repeat?.count;
      const patientInst = dosageData?.patientInstruction;
      const dosageInst = dosageData?.text;

      serviceData.push({
        id: index + 1,
        sequence: entry.sequence,
        nphiesCode: entry.productOrService?.coding?.[0]?.code || "",
        internalCode: entry.productOrService?.coding?.[1]?.code || "",
        nphiesDescription: entry.productOrService?.coding?.[0]?.display || "",
        internalDescription: entry.productOrService?.coding?.[1]?.display || "",
        date: moment(entry.servicedPeriod?.start).format("DD/MM/YYYY"),
        type: viewService?.servinfo_prdtyp,
        unitPrice: `${unitPrice.toFixed(2)} SAR`,
        discount: `${discount.toFixed(2)} SAR`,
        quantity: `${approvedQty || 0}`,
        patientShare: `${patientShare?.toFixed(2) || "0.00"} SAR`,
        netAmount: `${eligibleAmount?.toFixed(2) || "0.00"} SAR`,
        invoiceNo: invoiceNo?.valueIdentifier?.value || "-",
        vatAmount: `${vatExt?.valueMoney?.value?.toFixed(2) || "0.00"} SAR`,
        diagnosis: diagnosisCodes || "-",
        eligibilty: eligibilityAmt,
        benefit: benefitAmt,
        toothNo,
        practitioner: practitioner?.[index]?.resource?.name?.[0]?.text || "-",
        isMaternity: maternityExt?.valueBoolean === "true" ? "Yes" : "No",
        status: status,
        rejectionReasons: rejectionReasons,
        hasError: errorMsg,
        medCode,
        strengthValue,
        substitutePharmacy,
        otherSubstituePharmacy,
        period_start: periodStartDate,
        period_end: periodEndDate,
        duration,
        duration_type: durationType,
        frequency,
        period,
        dosageInst,
        period_type: periodType,
        selectReason,
        dose,
        dose_type: doseType,
        time_inst: timeInstruction,
        refill,
        patient_inst: patientInst,
        absenceRsn,
        claimMedicalDisplay,
        claimRoute,
      });
    });

    claimResponse?.item?.forEach((entry) => {
      const sequence = entry.itemSequence;
      const errorCode = entry.adjudication?.find(
        (adj) => adj.reason?.coding?.length > 0
      )?.reason?.coding?.[0]?.code;
      if (errorCode) {
        errorCodes[sequence] = errorCode;
      }
    });

    return { serviceData, itemErrorCodes: errorCodes };
  }, [claimResource, claimResponse, practitioner]);

  // Fetch error messages only for items with errors
  useEffect(() => {
    const fetchItemErrors = async () => {
      const newErrorMessages = {};

      // Run all dispatch calls in parallel (one per error code)
      const promises = Object.entries(itemErrorCodes).map(
        async ([sequence, errorCode]) => {
          try {
            const result = await dispatch(
              GetItemError({ error_code: errorCode })
            ).unwrap();

            const message =
              result?.data?.error_code?.[0]?.errcde_description || "";

            newErrorMessages[sequence] = message;

            setErrorMsg(!!message);

            // dispatch only unique messages

            dispatch(setErrorServices(message));
          } catch (err) {
            newErrorMessages[sequence] = `Unknown error (${errorCode})`;
          }
        }
      );

      await Promise.all(promises); // wait for all to finish
      setItemErrorMessages(newErrorMessages);
    };

    if (Object.keys(itemErrorCodes).length > 0) {
      fetchItemErrors();
    }
  }, [itemErrorCodes, dispatch]);

  console.log({ errorMsg });

  // Calculate adjudication totals
  const { totalEligible, totalBenefit, totalCopay, totalNet, totalVat } =
    useMemo(() => {
      const totals = {
        eligible: 0,
        benefit: 0,
        copay: 0,
        net: 0,
        vat: 0,
      };

      if (claimResponse?.item) {
        claimResponse.item.forEach((item) => {
          item.adjudication?.forEach((adj) => {
            const code = adj.category?.coding?.[0]?.code;
            const value = adj.amount?.value || 0;

            if (code && ["eligible", "benefit", "copay"].includes(code)) {
              totals[code] += value;
            }
          });
        });
      }

      if (serviceData?.length > 0) {
        totals.net = serviceData.reduce((sum, item) => {
          const net = parseFloat(item.netAmount.replace(" SAR", "")) || 0;
          return sum + net;
        }, 0);
        totals.vat = serviceData.reduce((sum, item) => {
          const vat = parseFloat(item.vatAmount.replace(" SAR", "")) || 0;
          return sum + vat;
        }, 0);
      }

      return {
        totalEligible: totals.eligible,
        totalBenefit: totals.benefit,
        totalCopay: totals.copay,
        totalNet: totals.net,
        totalVat: totals.vat,
      };
    }, [claimResponse, serviceData]);

  // Updated getStatusInfo to prioritize row.preauthsts === "cancelled"
  const getStatusInfo = (itemStatus) => {
    let statusValue = "";
    let statusStyle = { backgroundColor: "#f8f9fa", color: "#000" };

    // Check if preauthorization status is cancelled first
    if (row?.preauthsts?.toLowerCase() || row?.claimsts?.toLowerCase()) {
      statusValue = (row?.preauthsts || row?.claimsts || "").toLowerCase();
    } else if (
      viewPreauthorizationInfo?.preauth_status?.toLowerCase() === "cancelled" ||
      viewClaimInfo?.claim_status?.toLowerCase() === "cancelled"
    ) {
      statusValue = "cancelled";
    } else if (itemStatus) {
      // Use itemStatus if provided and preauthsts is not cancelled
      statusValue = itemStatus.toLowerCase();
    } else if (row?.preauthsts) {
      statusValue = row?.preauthsts.toLowerCase();
    } else if (viewClaimInfo?.claim_status) {
      statusValue = viewClaimInfo.claim_status.toLowerCase();
    } else if (viewPreauthorizationInfo?.preauth_status) {
      statusValue = viewPreauthorizationInfo.preauth_status.toLowerCase();
    } else if (claimResponse?.outcome) {
      statusValue = claimResponse.outcome.toLowerCase();
      if (statusValue === "complete") statusValue = "cancelled";
    } else {
      statusValue = "";
    }

    // Map status to corresponding styles
    const statusStyles = {
      error: { backgroundColor: "#fee2e2", color: "#dc2626" },
      rejected: { backgroundColor: "#fee2e2", color: "#dc2626" },
      noresp: { backgroundColor: "#e5e7eb", color: "#374151" },
      approved: { backgroundColor: "#dcfce7", color: "#16a34a" },
      complete: { backgroundColor: "#dcfce7", color: "#16a34a" },
      cancelled: { backgroundColor: "#fce7f6", color: "#db2777" },
      pended: { backgroundColor: "#dbeafe", color: "#2563eb" },
      pending: { backgroundColor: "#dbeafe", color: "#2563eb" },
      queued: { backgroundColor: "#fef9c3", color: "#ca8a04" },
      partial: { backgroundColor: "#cffafe", color: "#0891b2" },
      adjudication: { backgroundColor: "#ede9fe", color: "#7c3aed" },
      "not-required": { backgroundColor: "#e0f2fe", color: "#0284c7" },
    };

    if (statusValue && statusStyles[statusValue]) {
      statusStyle = statusStyles[statusValue];
    }

    return { statusValue, statusStyle };
  };

  // Render status badge using getStatusInfo
  const renderStatusBadge = useCallback(
    (status) => {
      const { statusValue, statusStyle } = getStatusInfo(status);
      return (
        <div
          className="d-inline-block rounded-pill px-2 py-1"
          style={{
            backgroundColor: statusStyle.backgroundColor,
            color: statusStyle.color,
            fontSize: "0.75rem",
            fontWeight: 500,
            border: "1px solid transparent",
            borderColor: "rgba(0,0,0,0.1)",
          }}
        >
          {(!show &&
            viewPreauthorizationInfo?.preauth_status?.toLowerCase() ===
            "cancelled") ||
            (show && row?.preauthsts?.toLowerCase() === "cancelled")
            ? "Cancelled"
            : statusValue.toUpperCase()}
        </div>
      );
    },
    [show, row, viewPreauthorizationInfo]
  );

  // Diagnosis for advanced preauthorization
  const diagnosisArray = useMemo(
    () =>
      advancedClaimResponse?.extension
        ?.filter(
          (ext) =>
            ext.url ===
            "http://nphies.sa/fhir/ksa/nphies-fs/StructureDefinition/extension-diagnosis"
        )
        .map((ext) => {
          const sequence = ext.extension?.find((e) =>
            e.url.includes("extension-diagnosis-sequence")
          )?.valuePositiveInt;

          const type = ext.extension?.find((e) =>
            e.url.includes("extension-diagnosis-type")
          )?.valueCodeableConcept?.coding?.[0];

          const diagnosis = ext.extension?.find((e) =>
            e.url.includes("extension-diagnosis-diagnosisCodeableConcept")
          )?.valueCodeableConcept?.coding?.[0];

          return {
            sequence,
            type: type?.display || type?.code || "N/A",
            code: diagnosis?.code,
            display: diagnosis?.display,
          };
        }) || [],
    [advancedClaimResponse]
  );

  // Fetch diagnosis descriptions
  useEffect(() => {
    const fetchDescriptions = async () => {
      for (const item of diagnosisArray) {
        const code = item.code; // Use item.code directly as per the structure

        if (code && !diagnosisDescriptions[code]) {
          try {
            const result = await dispatch(
              GetServiceCodeDescription({ diag_code: code })
            ).unwrap();
            const descriptionEntry = result?.data?.Diagnosis_code?.find(
              (entry) => entry.icd_code === code
            );
            setDiagnosisDescriptions((prev) => ({
              ...prev,
              [code]: descriptionEntry?.icd_description || "N/A",
            }));
          } catch (err) {
            setDiagnosisDescriptions((prev) => ({
              ...prev,
              [code]: "Description not found",
            }));
          }
        }
      }
    };

    if (diagnosisArray?.length > 0) {
      fetchDescriptions();
    }
  }, [diagnosisArray, diagnosisDescriptions, dispatch]);

  // Render service items
  const renderServiceItems = useMemo(() => {
    if (!serviceData?.length) return null;

    return serviceData.map((item) => {
      const [dayStr, monthStr, yearStr] = item.date.split("/");
      const dateObj = new Date(`${yearStr}-${monthStr}-${dayStr}`);
      const formatter = new Intl.DateTimeFormat("en", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });
      const parts = formatter.formatToParts(dateObj);
      const day = parts.find((p) => p.type === "day")?.value;
      const month = parts.find((p) => p.type === "month")?.value;
      const year = parts.find((p) => p.type === "year")?.value;

      const itemErrorMessage = itemErrorMessages[item.sequence] || null;

      console.log({ itemErrorMessage, itemErrorMessages, seq: item.sequence });
      const hasPrescriptionDetails =
        (item.medCode && item.medCode !== "-") ||
        item.claimMedicalDisplay ||
        (item.strengthValue && item.strengthValue !== "-") ||
        (item.absenceRsn && item.absenceRsn !== "-") ||
        (item.selectReason && item.selectReason !== "-") ||
        (item.otherSubstituePharmacy && item.otherSubstituePharmacy !== "-") ||
        (item.substitutePharmacy && item.substitutePharmacy !== "-") ||
        (item.period_start && item.period_end) ||
        item.duration ||
        (item.frequency && item.period && item.period_type) ||
        (item.dose && item.dose_type) ||
        item.claimRoute ||
        item.time_inst ||
        item?.dosageInst ||
        item.refill ||
        item.patient_inst;
      return (
        <Col xs={12} key={item.id} className="mb-3">
          <Card className="p-3 h-100">
            <Row className="g-3">
              <Col xs={12} md={2} className="text-center">
                <div className="category-sevie badge-subtle-success text-success fs-10">
                  {item.id}
                </div>
                <div className="mt-3">
                  <Calendar month={month} day={day} year={year} />
                </div>
              </Col>
              <Col xs={12} md={10}>
                <Row>
                  <Col
                    xs={12}
                    className="d-flex flex-wrap justify-content-between gap-2 mb-2"
                  >
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <div className="badge badge-subtle-success p-2">
                        {item.nphiesCode}
                      </div>
                      <div className="fs-10">{item.nphiesDescription}</div>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <div className="badge badge-subtle-warning p-2">
                        {item.internalCode}
                      </div>
                      <div className="fs-10">{item.internalDescription}</div>
                    </div>
                    {renderStatusBadge(item.status)}
                  </Col>

                  <Col xs={12}>
                    <Row className="gy-2">
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">Type</span>
                          <span className="fs-10 text-600">{item.type}</span>
                        </div>
                      </Col>
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Unit Price
                          </span>
                          <span className="fs-10 text-600 text-warning">
                            {item.unitPrice}
                          </span>
                        </div>
                      </Col>
                      {/* <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Discount Amount
                          </span>
                          <span className="fs-10 text-600 text-warning">
                            {item.discount}
                          </span>
                        </div>
                      </Col> */}
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Approved Quantity
                          </span>
                          <span className="fs-10 text-600">
                            {item.quantity}
                          </span>
                        </div>
                      </Col>
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Patient Share
                          </span>
                          <span className="fs-10 text-600 text-success">
                            {item.patientShare}
                          </span>
                        </div>
                      </Col>
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Eligible Amount
                          </span>
                          <span className="fs-10 text-600 text-primary">
                            {item.eligibilty}
                          </span>
                        </div>
                      </Col>
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Benefit Amount
                          </span>
                          <span className="fs-10 text-600 text-primary">
                            {item.benefit}
                          </span>
                        </div>
                      </Col>
                      {item.invoiceNo && item.invoiceNo !== "-" && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-800 fw-bold">
                              Invoice No
                            </span>
                            <span className="fs-10 text-600 text-info">
                              {item.invoiceNo}
                            </span>
                          </div>
                        </Col>
                      )}
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Tooth No
                          </span>
                          <span className="fs-10 text-600 text-danger">
                            {item.toothNo}
                          </span>
                        </div>
                      </Col>
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Diagnosis
                          </span>
                          <span className="fs-10 text-600">
                            {item.diagnosis}
                          </span>
                        </div>
                      </Col>
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Practitioner
                          </span>
                          <span className="fs-10 text-600">
                            {item.practitioner}
                          </span>
                        </div>
                      </Col>
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Is Maternity
                          </span>
                          <span className="fs-10 text-600">
                            {item.isMaternity}
                          </span>
                        </div>
                      </Col>

                      {hasPrescriptionDetails && (
                        <div className="fs-9 text-success fw-bold mb-2 border-bottom py-2">
                          Prescription Details
                        </div>
                      )}
                      {item.medCode && item.medCode !== "-" && (
                        <Col xs={6} md={2}>
                          <div className="d-flex  flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Prescribed Medication Code
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {item.medCode}
                            </span>
                          </div>
                        </Col>
                      )}
                      {item.claimMedicalDisplay && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Prescribed Medication Desc.
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {item.claimMedicalDisplay}
                            </span>
                          </div>
                        </Col>
                      )}
                      {item.strengthValue && item.strengthValue !== "-" && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Strength
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {item.strengthValue}
                            </span>
                          </div>
                        </Col>
                      )}
                      {item.absenceRsn && item.absenceRsn !== "-" && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Absence Reason
                            </span>
                            <span className="fs-10 text-capitalize text-info-emphasis">
                              {item.absenceRsn}
                            </span>
                          </div>
                        </Col>
                      )}

                      {item.selectReason && item.selectReason !== "-" && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Selection Reason
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {item.selectReason}
                            </span>
                          </div>
                        </Col>
                      )}
                      {item.otherSubstituePharmacy &&
                        item.otherSubstituePharmacy !== "-" && (
                          <Col xs={6} md={2}>
                            <div className="d-flex flex-column">
                              <span className="fs-10 text-info fw-bold">
                                Pharmacist Substitute
                              </span>
                              <div className="d-flex flex-column">
                                <span className="fs-10 text-info-emphasis">
                                  {item.otherSubstituePharmacy}
                                </span>
                                {item.substitutePharmacy &&
                                  item.substitutePharmacy != "-" && (
                                    <span className="fs-10 text-info-emphasis">
                                      ({item.substitutePharmacy})
                                    </span>
                                  )}
                              </div>
                            </div>
                          </Col>
                        )}

                      {item?.period_start && item?.period_end && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Period
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {`${item?.period_start} - ${item?.period_end}`}
                            </span>
                          </div>
                        </Col>
                      )}
                      {item?.duration && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Duration
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {`${item?.duration} ${item?.duration_type || "d"
                                }`}
                            </span>
                          </div>
                        </Col>
                      )}
                      {item?.duration && item?.period && item?.period_type && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Frequency
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {`(${item?.frequency}) - ${item?.period} ${item?.period_type || ""
                                }`}
                            </span>
                          </div>
                        </Col>
                      )}
                      {item?.dose && item?.dose_type && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Dose
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {`${item?.dose} ${item?.dose_type || ""}`}
                            </span>
                          </div>
                        </Col>
                      )}
                      {item.claimMedicalDisplay && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Route
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {item.claimRoute}
                            </span>
                          </div>
                        </Col>
                      )}
                      {item?.time_inst && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Time Instruction
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {item?.time_inst}
                            </span>
                          </div>
                        </Col>
                      )}
                      {item?.refill && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Refill
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {item?.refill}
                            </span>
                          </div>
                        </Col>
                      )}
                      {item?.dosageInst && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Dosage Instruction
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {item?.dosageInst}
                            </span>
                          </div>
                        </Col>
                      )}
                      {item?.patient_inst && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Patient Instruction
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {item?.patient_inst}
                            </span>
                          </div>
                        </Col>
                      )}

                      {errorMsg && (
                        <Col xs={12}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-800 fw-bold">
                              Rejection Reasons
                            </span>

                            {/* Error Item Messages (sequence-based) */}
                            {itemErrorMessage && (
                              <span className="fs-10 text-danger">
                                {itemErrorMessage || ""}
                              </span>
                            )}
                          </div>
                        </Col>
                      )}
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card>
        </Col>
      );
    });
  }, [serviceData, itemErrorMessages, renderStatusBadge]);

  // Render advanced items
  const renderAdvancedItems = useMemo(() => {
    if (!isAdvanced || !advancedClaimResponse?.addItem?.length) return null;

    return advancedClaimResponse.addItem.map((item, index) => (
      <Col xs={12} key={index} className="mb-3">
        <Card className="p-3 h-100">
          <Row className="g-3">
            <Col xs={12} md={2} className="text-center">
              <div className="category-sevie badge-subtle-success text-success fs-10">
                {index + 1}
              </div>
            </Col>
            <Col xs={12} md={10}>
              <Row>
                <Col
                  xs={8}
                  className="d-flex flex-wrap justify-content-between gap-2 mb-2"
                >
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <div className="badge badge-subtle-success p-2">
                      {item.productOrService?.coding?.[0]?.code}
                    </div>
                    <div className="fs-10">
                      {item.productOrService?.coding[0]?.display}
                    </div>
                  </div>
                </Col>
                <Col xs={2}>
                  <div className="d-flex flex-column">
                    <span className="fs-10 text-800 fw-bold">
                      Approved Quantity
                    </span>
                    <span className="fs-10 fw-bold text-success">
                      {item.adjudication?.[0]?.value || "N/A"}
                    </span>
                  </div>
                </Col>
                <Col xs={2}>{renderStatusBadge("approved")}</Col>
              </Row>
            </Col>
          </Row>
        </Card>
      </Col>
    ));
  }, [isAdvanced, advancedClaimResponse, renderStatusBadge]);

  // Render diagnosis section
  const renderDiagnosisSection = useMemo(() => {
    if (!isAdvanced || !diagnosisArray?.length) return null;

    return (
      <motion.div
        style={{ border: "2px solid #d9f8eb", borderRadius: "10px" }}
        className="p-3 position-relative mt-4 mb-3"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="position-absolute top-0 start-40 translate-middle-y d-none d-md-block">
          <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-info px-3 py-2">
            <span className="fs-11">Diagnosis</span>
          </span>
        </div>

        <div className="mt-4">
          {diagnosisArray.map((item, idx) => {
            const description = item.code
              ? diagnosisDescriptions[item.code] || "Loading..."
              : item.display || "N/A";

            return (
              <Card key={idx} className="mb-3 border-0 shadow-md">
                <Card.Body className="p-3">
                  <Row className="align-items-center">
                    <Col xs={2} className="text-center">
                      <div
                        className="rounded-circle bg-info text-white d-flex align-items-center justify-content-center"
                        style={{ width: "36px", height: "36px" }}
                      >
                        {idx + 1}
                      </div>
                    </Col>

                    <Col xs={3}>
                      <div className="d-flex flex-column gap-1">
                        <small className="fs-10 d-block">Diagnosis Type</small>
                        <strong className="fs-10 text-600">{item.type}</strong>
                      </div>
                    </Col>

                    <Col xs={7}>
                      <div>
                        <small className="fs-10 d-block">
                          Diagnosis Code Description
                        </small>
                        <div className="d-flex fs-10 text-600">
                          {item.code && (
                            <span className="pe-2">({item.code}) -</span>
                          )}
                          <span>{description}</span>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            );
          })}
        </div>
      </motion.div>
    );
  }, [isAdvanced, diagnosisArray, diagnosisDescriptions]);

  // Render totals section
  const renderTotalsSection = useMemo(() => {
    if (!serviceData?.length) return null;

    const total = totalNet + totalVat;

    return (
      <Row className="mt-4">
        <Col xs={12}>
          <Card className="p-3">
            <Row className="g-3">
              <Col xs={12} md={4} className="text-start">
                <div className="d-flex flex-column">
                  <span className="fs-10 text-800 fw-bold">
                    Eligible Amount
                  </span>
                  <span className="fs-10 fw-bold text-primary">
                    {totalEligible.toFixed(2)} SAR
                  </span>
                </div>
              </Col>
              <Col xs={12} md={4} className="text-start">
                <div className="d-flex flex-column">
                  <span className="fs-10 text-800 fw-bold">Benefit Amount</span>
                  <span className="fs-10 fw-bold text-primary">
                    {totalBenefit.toFixed(2)} SAR
                  </span>
                </div>
              </Col>
              <Col xs={12} md={4} className="text-start">
                <div className="d-flex flex-column">
                  <span className="fs-10 text-800 fw-bold">Patient Share</span>
                  <span className="fs-10 fw-bold text-success">
                    {totalCopay.toFixed(2)} SAR
                  </span>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    );
  }, [serviceData, totalNet, totalVat]);

  return (
    <>
      <motion.div
        style={{ border: "2px solid #d9f8eb" }}
        className="rounded p-3 position-relative mt-3 mb-3"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        transition={{ duration: 0.5 }}
      >
        <div className="position-absolute top-0 start-40 translate-middle-y d-none d-md-block">
          <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-success px-3 py-2">
            <span className="fs-11">Service & Procedures</span>
          </span>
        </div>

        {!isAdvanced && renderServiceItems}
        {isAdvanced && renderAdvancedItems}
        {!isAdvanced && renderTotalsSection}
      </motion.div>

      {renderDiagnosisSection}
    </>
  );
};

export default ServicesProceduresPayerResponse;
export const timeInstructionData = [
  {
    time_instCode: "BID",
    time_instDisplay: "Two time a day",
  },
  {
    time_instCode: "TID",
    time_instDisplay: "Three time a day",
  },
  {
    time_instCode: "QID",
    time_instDisplay: "Four time a day",
  },
  {
    time_instCode: "AM",
    time_instDisplay: "Every Morning",
  },
  {
    time_instCode: "PM",
    time_instDisplay: "Every Afternoon",
  },
  {
    time_instCode: "QD",
    time_instDisplay: "Every day",
  },
  {
    time_instCode: "QOD",
    time_instDisplay: "Every Other day",
  },
];
